import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { match as Match } from "react-router-dom";
import { Location } from "history";
import { injectIntl, IntlShape } from "react-intl";
import { toast } from "react-toastify";
import { TableChangeParams } from "../../../../components/Table/types";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import PageSubheader from "../../../../components/PageSubheader";
import { EnumPaths } from "../../../../utils/navigation";
import history from "../../../../history";
import Table from "../../../../components/Table";
import Dropdown, {
  DropdownValues,
} from "../../../../components/Tailwind/Dropdown";
import images from "../../../../images";
import { getTranslation } from "../../../../utils/translation";
import ConfirmModal from "../../../../utils/modal/confirm";
import IntlMessages from "../../../../utils/messages";
import {
  displayDayDDMMYYYY_HHMM,
  DEFAULT_LIMIT,
} from "../../../../utils/config";
import {
  CheckpointType,
  SearchableCheckpointTypesFilterInput,
} from "../../../../lib/ground-aws-graphql-core/models/Api/Inspection";
import Badge from "../../../../components/Tailwind/Badge";

interface Props {
  intl: IntlShape;
  match: Match<{ cid: string }>;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
  }>;
}

const ListCheckpointTypes = (props: Props): JSX.Element => {
  const { intl, location, match } = props;
  const [loading, setLoading] = useState(false);

  const [showDeleteCheckpointTypeModal, setShowDeleteCheckpointTypeModal] =
    useState(false);

  const [checkpointTypeId, setCheckpointTypeId] = useState<number>();
  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state
  );

  const listCheckpointTypes = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.listCheckpointTypes
  );

  const checkpointTypes = GroundGraphqlContextStore.useStoreState(
    actions => actions.inspection.checkpointTypes.items
  );

  const total = GroundGraphqlContextStore.useStoreState(
    actions => actions.inspection.checkpointTypes.total
  );

  const setCheckpointType = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.setCheckpointType
  );

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  const deleteCheckpointType = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.deleteCheckpointType
  );

  useEffect(() => {
    if (location?.state) {
      fetchData(location?.state?.pageIndex, true, location?.state?.limit);
    } else fetchData();
  }, []);

  const fetchData = (pageIndex = 0, loader = true, limit = DEFAULT_LIMIT) => {
    if (loader) setLoading(true);

    const filter: SearchableCheckpointTypesFilterInput = {};
    filter.building_id = { eq: match.params.cid };
    listCheckpointTypes({
      offset: pageIndex * limit,
      limit,
      filter,
    }).finally(() => setLoading(false));
  };

  const addNewCheckpointType = () => {
    history.push(
      `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}/${EnumPaths.CREATION_MODE}`
    );
  };

  const handleOnChange = (changeParams: TableChangeParams) => {
    const { pageIndex, limit } = changeParams;
    setInitialParams(changeParams);
    fetchData(pageIndex, false, limit);
  };

  const handleEditCheckpointType = (id: number) => {
    const editLink = `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}/${id}/${EnumPaths.EDIT_MODE}`;
    setCheckpointType(null);
    history.push(editLink);
  };

  const handleDelete = () => {
    setLoading(true);
    setShowDeleteCheckpointTypeModal(false);
    deleteCheckpointType({ id: checkpointTypeId! })
      .then(() => {
        toast(
          intl.formatMessage({
            id: "page.inspection.type.delete.success",
          }),
          { type: "success" }
        );
      })
      .catch(() => {
        toast(
          intl.formatMessage({
            id: "page.inspection.type.delete.error",
          }),
          {
            type: "error",
          }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const tableHead = [
    "page.inspection.type.label",
    "page.inspection.type.created_at",
    "page.inspection.type.updated_at",
    "general.status",
    "general.actions",
  ];

  const tableBody = checkpointTypes.map((checkpointType: CheckpointType) => {
    const { id, title, created_at, updated_at, enabled } = checkpointType;
    const editLink = `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}/${id}/${EnumPaths.EDIT_MODE}`;

    const dropdownValues: DropdownValues[] = [
      {
        id: "edit_inspection_type_key",
        label: "page.list.inspection.types.edit",
        icon: images.edit,
        link: editLink,
      },
      {
        id: "delete",
        label: "general.delete",
        icon: images.deleteIcon,
        onClick: () => {
          setShowDeleteCheckpointTypeModal(!showDeleteCheckpointTypeModal);
          setCheckpointTypeId(id);
        },
      },
    ];

    const elements = [
      {
        element: getTranslation(title),
        onCellClick: () => handleEditCheckpointType(id),
      },
      {
        element: displayDayDDMMYYYY_HHMM(created_at, centerTimezone),
        onCellClick: () => handleEditCheckpointType(id),
      },
      {
        element: displayDayDDMMYYYY_HHMM(updated_at, centerTimezone),
        onCellClick: () => handleEditCheckpointType(id),
      },
      {
        element: enabled ? (
          <Badge label="general.actif" bgClassName="bg-ground-green-100" />
        ) : (
          <Badge label="general.inactif" bgClassName="bg-red-500" />
        ),
        onCellClick: () => handleEditCheckpointType(id),
      },
      {
        element: (
          <Dropdown
            values={dropdownValues}
            dataCy={`inspection-type-dropdown-${id}`}
          />
        ),
      },
    ];

    return {
      id: checkpointType.id.toString(),
      rowElements: elements,
    };
  });

  return (
    <div>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: `page.list.inspection.types.document.title`,
          })}
        </title>
      </Helmet>

      <PageSubheader
        title="page.list.inspection.types.title"
        nbOfResults={total}
        buttonRightTitle="page.list.inspection.types.create.checkpoint.type"
        buttonRightAction={addNewCheckpointType}
        buttonRightId="btn-create-checkpoint-type"
      />

      <Table
        initialParams={initialParams}
        head={tableHead}
        body={tableBody}
        noDataText="page.list.inspection.types.empty"
        onChange={handleOnChange}
        paginationLimit={DEFAULT_LIMIT}
        paginationTotal={total}
        loading={loading}
        setLoading={setLoading}
        className="px-8"
      />

      <ConfirmModal
        isOpen={showDeleteCheckpointTypeModal}
        onRequestClose={() =>
          setShowDeleteCheckpointTypeModal(!showDeleteCheckpointTypeModal)
        }
        toggle={() =>
          setShowDeleteCheckpointTypeModal(!showDeleteCheckpointTypeModal)
        }
        handleConfirm={handleDelete}
        content={<IntlMessages id="page.list.inspection.types.delete" />}
      />
    </div>
  );
};

export default injectIntl(ListCheckpointTypes);
